import React, { useState, useRef, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@app/src/common/Icon';
import { COLORS } from '@app/src/ui';
import {
  Wrapper,
  IconWrapper,
  Ubigeo,
  Placeholder,
  Content,
  IconClose,
  LoaderWrapper,
  InputWrapper
 } from './styled';
import { PredictiveResults } from './PredictiveResults';
import { isClientEnv } from '@app/src/utils/validations';
import { PredictiveContext } from './PredictiveConstructor';
import { fetchDistrictAction, clearDistrictData } from '@app/src/views/StepOne/form';

const isClient = isClientEnv();
const KEY_DOWN = 40;
const KEY_UP = 38;
const ESC = 27;
const ENTER = 13;
const MIN_CHARACTERS = 3;

export const SearchInputCustom: React.FC = () => {

  const {
    handleChange,
    handleChangeInput,
    error,
    handleBlurInput,
    defaultUbigeo,
    disabled,
    input,
    handleClickClose
  } = useContext(PredictiveContext);
  const dispatch = useDispatch();
  const isLoading = useSelector(state => state.district.isFetching);
  const [suggestionsList, setSuggestionsList] = useState([]);
  const [currentValue, setCurrentValue] = useState('');
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [formatValue, setFormatValue] = useState([]);
  const [value, setValue] = useState('');
  const messageUbigeo = useSelector(state => state.district.message);
  const refWrapper = useRef(null);
  const refClose = useRef(null);
  const  refInput = useRef(null);
  let refItems = null;
  const districts = useSelector(state => state.district.data);
  const inputValue = useSelector(state => state.district.inputValue);

  useEffect(() => {
    if (currentValue.length >= MIN_CHARACTERS && currentValue !== value && !isLoading) {
        setSuggestionsList(districts);
        refInput.current.focus();
    }
    if (currentValue.length < MIN_CHARACTERS) {
      setSuggestionsList([]);
    }
  }, [districts, currentValue, value, isLoading])

  useEffect(() => {
    if (value) {
      let formatValue = value.split(',');
      setFormatValue(formatValue)
    }
    if (currentValue.length >= MIN_CHARACTERS && currentValue !== value && !isLoading) {
      dispatch(fetchDistrictAction(currentValue));
    }
  }, [currentValue, value]);

  useEffect(() => {
    if (currentValue !== inputValue && inputValue !== '' && isLoading) {
      dispatch(fetchDistrictAction(currentValue));
    }
  }, [inputValue]);

  useEffect(() => {
    if (defaultUbigeo) {
      setValue(defaultUbigeo);
      setCurrentValue(defaultUbigeo);
      refInput.current.style.display ='none';
    } else {
      setValue('');
      setCurrentValue('');
      refInput.current.style.display ='block';
    }
  }, [defaultUbigeo]);

  useEffect(() => {
    if(isClient) {
      if(suggestionsList && suggestionsList.length > 0) {
        document.addEventListener('click', handleCloseSuggestion);
      }
      return () => {
        document.removeEventListener('click', handleCloseSuggestion);
      };
    }
  }, [suggestionsList]);

  useEffect(() => {
    if(suggestionsList[activeSuggestion]) {
      setCurrentValue(suggestionsList[activeSuggestion].data.location);
      setValue(suggestionsList[activeSuggestion].data.location);
      refItems[activeSuggestion].current.focus();
      refInput.current.style.display ='none';
    }
    if(activeSuggestion < 0) {
      setSuggestionsList([]);
    }

  }, [activeSuggestion]);

  const handleCloseSuggestion = (e: MouseEvent) => {
    if(!refWrapper.current.contains(e.target)) {
      setSuggestionsList([]);
    }
  };

  const handleClickClear = () => {
    handleClickClose && handleClickClose();
    setSuggestionsList([])
    dispatch(clearDistrictData())
    setCurrentValue('');
    setValue('');
  };

  const handleClickSuggestion = (indexActive: number) => {
    setActiveSuggestion(indexActive);
    refInput.current.style.display ='none';
    handleChange && handleChange(suggestionsList[indexActive]);
  };

  const handleKeyDownInput = (e: React.KeyboardEvent) => {
    const keyCode = e.keyCode || e.which;
    if(keyCode === KEY_DOWN) {
      if(suggestionsList.length) {
        setActiveSuggestion(0);
      }
    }
    if(keyCode === ENTER) {
      e.preventDefault();
      setSuggestionsList([]);
    }
  };

  const handleKeyDownList = (e: React.KeyboardEvent) => {
    e.preventDefault();
    const keyCode = e.keyCode || e.which;
    if(keyCode === KEY_DOWN) {
      if(activeSuggestion < suggestionsList.length - 1) {
        setActiveSuggestion(activeSuggestion + 1);
      }
    }
    if(keyCode === KEY_UP) {
      setActiveSuggestion(activeSuggestion - 1);
    }
    if(keyCode === ENTER) {
      setActiveSuggestion(activeSuggestion);
      handleChange && handleChange(suggestionsList[activeSuggestion]);
      setActiveSuggestion(-1);
    }
    if(keyCode === ESC) {
      setSuggestionsList([]);
    }
  };

  const handleBlur = () => {
    if(activeSuggestion === -1) {
      setSuggestionsList([]);
      dispatch(clearDistrictData());
      setValue('');
      handleChange && handleChange(null);
      handleBlurInput && handleBlurInput();
    }
  };

  const handleChangeProp = (e) => {
    setCurrentValue(e.target.value.replace(/\s\s+|\n/g, ' '));
    handleChangeInput && handleChangeInput(e);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return(
    <Content>
      <Wrapper
        ref={refWrapper}
        borderColor={`1px solid ${error ? 'red' : COLORS.GRAY}`}
      >
        <IconWrapper>
          <Icon data-name="icon-map" width="18" height="18" fill={COLORS.GRAY_PLACEHOLDER} />
        </IconWrapper>
        <InputWrapper
          {...input}
          onBlur={handleBlur}
          onChange={handleChangeProp}
          ref={refInput}
          disabled={disabled}
          value={currentValue}
          onKeyPress={handleKeyPress}
          onKeyDown={handleKeyDownInput}
          autoComplete="off"
        />
        {
          (currentValue === '' && value === '') &&
            <Placeholder>
              <span>Ej. Miraflores</span>, Lima, Lima
            </Placeholder>
        }
        {
          (currentValue && currentValue === value) &&
            <Ubigeo>
              <span>{formatValue[0]}</span>{formatValue[1] ? `, ${formatValue[1]}` : ''}{formatValue[2] ? `, ${formatValue[2]}` : ''}
            </Ubigeo>
        }
        {
          (currentValue !== '' || value !== '') && !disabled && !isLoading &&
            <IconClose onClick={handleClickClear} ref={refClose}>
              <Icon data-name="icon-close-bold" width="18" height="18" fill={COLORS.GRAY} />
            </IconClose>
        }
        {
          isLoading &&
          <LoaderWrapper>
              <Icon data-name='icon-circle' fill='#ccc' width={'17'} height={'17'} />
          </LoaderWrapper>
        }
      </Wrapper>
      {
        <PredictiveResults
          dataList={suggestionsList}
          showFirstItems={5}
          handleClickItem={handleClickSuggestion}
          handleRefItems={refs => { refItems = refs }}
          handleKeyDown={handleKeyDownList}
          message={messageUbigeo}
        />
      }
    </Content>
)};
